<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue';
import HugeIcon from '../HugeIcon/HugeIcon.vue';
import type { ButtonV2HideLabelBelow, ButtonV2Props } from './button-v2';
import TTooltip from '../TTooltip.vue';

const props = withDefaults(defineProps<ButtonV2Props>(), {
  variant: 'primary',
});

const emits = defineEmits<{
  click: [event: MouseEvent];
}>();

const buttonRef = ref<HTMLButtonElement | HTMLAnchorElement | null>(null);
const isDisabled = computed((): boolean => props.disabled || props.loading);

const handleClick = (event: MouseEvent): void => {
  if (isDisabled.value) {
    event.preventDefault();
    return;
  }
  emits('click', event);
};

onMounted(() => {
  if (!props.autofocus) {
    return;
  }

  // Run after Headless UI Dialog initial focus so confirm actions receive Enter.
  void nextTick(() => {
    buttonRef.value?.focus();
  });
});

const getHideLabelClass = (threshold: ButtonV2HideLabelBelow): string => {
  switch (threshold) {
    case 'sm':
      return 'hidden sm:block';
    case 'md':
      return 'hidden md:block';
    case 'lg':
      return 'hidden lg:block';
  }
};

const hideLabelBelow = computed<ButtonV2HideLabelBelow | undefined>(() => {
  return (props as { hideLabelBelow?: ButtonV2HideLabelBelow }).hideLabelBelow;
});

const labelVisibilityClass = computed(() => {
  if (hideLabelBelow.value) {
    return getHideLabelClass(hideLabelBelow.value);
  }

  return 'block';
});
</script>

<template>
  <TTooltip :content="props.tooltip" placement="top">
    <component
      :is="props.href ? 'a' : 'button'"
      ref="buttonRef"
      :aria-label="props.label"
      :type="props.href ? undefined : props.type || 'button'"
      :form="props.href ? undefined : props.formId"
      :disabled="props.href ? undefined : isDisabled"
      :href="isDisabled ? undefined : props.href"
      :target="props.href ? props.target : undefined"
      :rel="
        props.href
          ? (props.rel ??
            (props.target === '_blank' ? 'noopener noreferrer' : undefined))
          : undefined
      "
      :aria-disabled="props.href && isDisabled ? true : undefined"
      :tabindex="props.href && isDisabled ? -1 : undefined"
      data-button-v2="true"
      class="relative touch-manipulation text-nowrap disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
      :class="[
        'inline-flex items-center gap-1 rounded-lg font-semibold transition-all duration-300',
        props.size === 'md'
          ? [
              'text-md h-10 py-2',
              props.iconOnly ? 'w-10 justify-center px-2' : 'px-4',
            ]
          : [
              'h-8 py-1.5 text-sm',
              props.iconOnly ? 'w-8 justify-center px-1.5' : 'px-3',
            ],
        props.variant === 'primary' &&
          'bg-gradient-to-tr from-violet-600 to-indigo-600 text-white shadow-[0_4px_15px_rgba(147,51,234,0.4)] ease-[cubic-bezier(0.4,0,0.2,1)] hover:translate-y-[-1px] hover:shadow-[0_8px_25px_rgba(147,51,234,0.5)] hover:brightness-110 active:translate-y-[1px] active:shadow-[0_2px_10px_rgba(147,51,234,0.3)]',
        props.variant === 'secondary' &&
          'border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-100 hover:text-primary-800 border shadow-sm active:translate-y-[1px]',
        props.variant === 'assistant' && 'button-v2-assistant',
        props.variant === 'assistant-light' && 'button-v2-assistant-light',
        props.variant === 'danger' &&
          'bg-danger-500 text-white hover:brightness-110 active:translate-y-[1px]',
        props.variant === 'neutral' &&
          'border border-slate-200 bg-slate-50 text-slate-800',
        props.variant === 'ghost-primary' &&
          'text-primary-500 hover:bg-primary-50',
        props.variant === 'ghost-neutral' &&
          'text-neutral-500 hover:bg-neutral-50 hover:text-neutral-800',
      ]"
      @click="handleClick"
    >
      <slot name="content" :icon-only="props.iconOnly" :loading="props.loading">
        <HugeIcon
          v-if="props.icon && props.iconPosition !== 'right'"
          :spin="props.loading"
          :icon="props.loading ? 'loading' : props.icon"
          :size="props.size === 'sm' ? 20 : 24"
        />
        <!-- Wrap label in a span that can shrink (min-w-0) and apply
      truncate so text-overflow: ellipsis works inside flex containers -->
        <span
          class="max-w-full min-w-0 truncate"
          :class="props.iconOnly ? 'sr-only' : labelVisibilityClass"
          >{{ props.label }}</span
        >
        <HugeIcon
          v-if="props.icon && props.iconPosition === 'right'"
          :spin="props.loading"
          :icon="props.loading ? 'loading' : props.icon"
          :size="props.size === 'sm' ? 20 : 24"
        />
      </slot>
    </component>
  </TTooltip>
</template>

<style scoped>
.button-v2-assistant {
  background-image: linear-gradient(
    135deg,
    var(--color-primary-600),
    var(--color-primary-500) 42%,
    var(--color-secondary-500)
  );
  color: var(--color-white-500);
  box-shadow:
    0 4px 14px -4px
      color-mix(in srgb, var(--color-primary-600) 55%, transparent),
    0 0 0 1px rgb(255 255 255 / 0.18) inset;
}

.button-v2-assistant:not(:disabled):hover {
  background-image: linear-gradient(
    135deg,
    var(--color-primary-700),
    var(--color-primary-600) 42%,
    var(--color-secondary-600)
  );
  translate: 0 -1px;
  box-shadow:
    0 8px 22px -6px
      color-mix(in srgb, var(--color-primary-600) 55%, transparent),
    0 0 0 1px rgb(255 255 255 / 0.22) inset;
}

.button-v2-assistant:not(:disabled):active {
  translate: 0 1px;
  box-shadow:
    0 2px 10px -4px
      color-mix(in srgb, var(--color-primary-600) 45%, transparent),
    0 0 0 1px rgb(255 255 255 / 0.16) inset;
}

.button-v2-assistant-light {
  color: var(--color-primary-700);
  border: 1px solid var(--color-primary-200);
  background-color: var(--color-primary-50);
  background-image: linear-gradient(
    114deg,
    var(--color-secondary-100) 0%,
    var(--color-primary-50) 52%,
    var(--color-primary-100) 100%
  );
  box-shadow:
    0 1px 2px rgb(0 0 0 / 0.04),
    0 4px 12px -4px
      color-mix(in srgb, var(--color-primary-400) 18%, transparent);
}

.button-v2-assistant-light:not(:disabled):hover {
  color: var(--color-primary-800);
  border-color: var(--color-primary-300);
  background-image: linear-gradient(
    114deg,
    var(--color-secondary-200) 0%,
    var(--color-primary-100) 52%,
    var(--color-primary-200) 100%
  );
  translate: 0 -1px;
  box-shadow:
    0 2px 4px rgb(0 0 0 / 0.05),
    0 8px 16px -6px
      color-mix(in srgb, var(--color-primary-400) 22%, transparent);
}

.button-v2-assistant-light:not(:disabled):active {
  translate: 0 1px;
  box-shadow:
    0 1px 2px rgb(0 0 0 / 0.04),
    0 2px 8px -4px color-mix(in srgb, var(--color-primary-400) 16%, transparent);
}
</style>
